import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, RefreshCw } from 'lucide-react'
import { useEffect, type ReactElement } from 'react'
import { Link, matchPath, Outlet, useLocation } from 'react-router'

import {
  parentReadDeviceDetailOptions,
  parentReadDevicesOptions,
  parentReadFamilyOptions,
} from '@/client/@tanstack/react-query.gen'
import { AccountSheet } from '@/components/features/account-sheet'
import { InstallHint } from '@/components/features/install-hint'
import { usePullToRefresh } from '@/components/features/use-pull-to-refresh'
import { OfflineScreen } from '@/components/features/offline-screen'
import { useOffline } from '@/components/features/use-offline'
import { parentQueryDefaults } from '@/lib/parent-query'

export function ParentLayout(): ReactElement {
  const location = useLocation()
  const deviceMatch = matchPath('/devices/:deviceId', location.pathname)

  const family = useQuery({ ...parentReadFamilyOptions(), ...parentQueryDefaults })
  const devices = useQuery({ ...parentReadDevicesOptions(), ...parentQueryDefaults })
  const device = useQuery({
    ...parentReadDeviceDetailOptions({
      path: { device_id: deviceMatch?.params.deviceId ?? '' },
    }),
    ...parentQueryDefaults,
    enabled: deviceMatch !== null,
  })

  const queryClient = useQueryClient()
  const { pull, refreshing } = usePullToRefresh(() =>
    queryClient.refetchQueries({ type: 'active' }),
  )

  const { offline, retry } = useOffline()

  useEffect(() => {
    document.documentElement.classList.add('parent-shell')
    return () => document.documentElement.classList.remove('parent-shell')
  }, [])

  const onDeviceScreen = deviceMatch !== null
  const singleDevice = devices.data?.length === 1

  return (
    <div className="bg-background min-h-svh">
      <header className="bg-background/95 sticky top-0 z-40 border-b backdrop-blur pb-2 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] pt-[calc(0.5rem+env(safe-area-inset-top))]">
        <div className="mx-auto flex h-8 max-w-md items-center justify-between gap-2">
          {onDeviceScreen && !singleDevice ? (
            <Link
              to="/"
              viewTransition
              aria-label={`‹ ${family.data?.name ?? 'Devices'}`}
              className="text-primary flex min-w-0 items-center gap-0.5 truncate active:opacity-70 [-webkit-tap-highlight-color:transparent]"
            >
              <ChevronLeft aria-hidden="true" className="size-5 shrink-0" />
              <span className="truncate">{family.data?.name ?? 'Devices'}</span>
            </Link>
          ) : (
            <span aria-hidden="true" className="w-16" />
          )}
          {onDeviceScreen && (
            <span className="text-muted-foreground min-w-0 truncate text-sm font-medium">
              {device.data?.name ?? ''}
            </span>
          )}
          <AccountSheet />
        </div>
      </header>
      {(pull > 0 || refreshing) && (
        <div
          role="status"
          aria-label={refreshing ? 'Refreshing' : 'Pull to refresh'}
          className="bg-background text-foreground pointer-events-none fixed left-1/2 z-50 mt-1 flex size-9 items-center justify-center rounded-full border shadow-sm"
          style={{ transform: `translate(-50%, ${pull}px)` }}
        >
          <RefreshCw
            aria-hidden="true"
            className={refreshing ? 'animate-spin size-4' : 'size-4'}
          />
        </div>
      )}
      {offline && (
        <div className="bg-background fixed inset-0 z-[60] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] pt-[env(safe-area-inset-top)]">
          <OfflineScreen onRetry={retry} />
        </div>
      )}
      <main className="mx-auto max-w-md space-y-6 p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <InstallHint />
        <Outlet />
      </main>
    </div>
  )
}
